import React, { useState, useRef, useEffect, Children, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

import FontAwesome from './FontAwesome';

import theme from './theme';

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translate3d(0, 20px, 0);
    }

    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
`;

interface IMenuProps {
    position?: Position;
}

export const Menu = styled.div<IMenuProps>`
    margin-top: 20px;
    position: absolute;
    display: flex;
    flex-direction: column;
    width: 200px;
    background-color: white;
    border: 1px solid ${theme.grey};
    box-shadow: 0 2px 5px 1px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    right: ${({ position }) => (position === 'left' ? 'unset' : '0')};
    left: ${({ position }) => (position === 'left' ? '0' : 'unset')};
    z-index: 5;
    animation: ${fadeInUp} 0.2s ease-out;

    :after {
        content: '';
        position: absolute;
        left: ${({ position }) => (position === 'left' ? '10%' : '82%')};
        bottom: 100%;
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 10px solid #fff;
        clear: both;
    }

    :before {
        content: '';
        position: absolute;
        left: ${({ position }) => (position === 'left' ? 'calc(10% - 2px)' : 'calc(82% - 2px)')};
        bottom: calc(100%);
        width: 0;
        height: 0;
        border-left: 12px solid transparent;
        border-right: 12px solid transparent;
        border-bottom: 12px solid ${theme.grey};
        clear: both;
    }
`;

export const DropDownLink = styled(Link)`
    display: flex;
    flex-direction: row;
    font-weight: 400;
    padding: 8px 16px;
    line-height: 1.4285;
    color: rgb(51, 51, 51);
    cursor: pointer;

    &:hover {
        color: #262626;
        text-decoration: none;
        background-color: #f5f5f5;
    }

    &:active {
        color: #262626;
        text-decoration: none;
        background-color: darken(#f5f5f5, 10%);
    }
`;

export const DropDownItem = styled.a`
    display: flex;
    flex-direction: row;
    font-weight: 400;
    padding: 8px 16px;
    line-height: 1.4285;
    color: rgb(51, 51, 51);
    cursor: pointer;

    &:hover {
        color: #262626;
        text-decoration: none;
        background-color: #f5f5f5;
    }
    &:active {
        color: #262626;
        text-decoration: none;
        background-color: darken(#f5f5f5, 10%);
    }
`;

interface IDropDownChoiceProps {
    value: string;
    current: string;
    label: string;
    onChange: (arg: string) => void;
    close?: () => void;
    ['data-testid']?:string;
}

export function DropDownChoice({
    value,
    current,
    label,
    onChange,
    close,
    ...props
}: IDropDownChoiceProps): ReactElement {
    return (
        <DropDownItem
            {...props['data-testid'] && {['data-testid']:props['data-testid'] }}
            onClick={() => {
                onChange(value);
                close && close();
            }}>
            <div style={{ width: 20, flexGrow: 0 }}>
                {value === current ? <FontAwesome icon="check" weight="regular" /> : ''}
            </div>
            <div style={{ flexGrow: 1 }}>{label}</div>
        </DropDownItem>
    );
}

const position = {
    right: 'right',
    left: 'left'
};

type Position = keyof typeof position;

type Style = { [key: string]: string | number };

interface IDropdownProps {
    button: ReactElement;
    children: ReactNode;
    menuStyle?: Style;
    style?: Style;
    position?: Position;
    ['data-testid']?: string;
}

export function DropDown({
    button,
    children,
    style,
    menuStyle,
    position = 'right',
    ...props
}: IDropdownProps): ReactElement {
    const [open, setOpen] = useState(false);
    const el = useRef<HTMLDivElement>(null);

    function toggleOpen() {
        setOpen(!open);
    }

    function handleClick(e) {
        if (el?.current?.contains(e.target)) {
            if (e.target.tagName === 'A') {
                setOpen(false);
            }
        } else {
            setOpen(false);
        }
    }

    useEffect(() => {
        if (open) {
            window.addEventListener('click', handleClick);
        }

        return () => window.removeEventListener('click', handleClick);
    }, [open]);

    const trigger = React.cloneElement(button, { onClick: toggleOpen });

    return (
        <div ref={el} style={{ position: 'relative', cursor: 'pointer', ...style }} {...props['data-testid'] && {['data-testid']:props['data-testid'] }}>
            {Children.count(children) ? trigger : button}
            {open ? (
                <Menu position={position} style={menuStyle}>
                    {Children.map(children, (child) =>
                        child
                            ? React.cloneElement(child as ReactElement, {
                                  close: () => setOpen(false)
                              })
                            : null
                    )}
                </Menu>
            ) : null}
        </div>
    );
}
